import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

const GRID = "#EDE7DC"
const MUTED = "#7A7570"

// Generic single-line chart. Data and formatting come from Stimulus values so the same
// controller drives volume (tonnes), 1RM and top-set (kg) charts on the Progress page.
// When `smooth` is set, a centered moving-average trend line is drawn over faint raw data.
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    labels: Array,
    points: Array,
    prs: Array,
    unit: { type: String, default: "raw" },
    color: { type: String, default: "#C67B5C" },
    fill: { type: Boolean, default: false },
    smooth: { type: Boolean, default: false },
    sparkline: { type: Boolean, default: false },
  }

  connect() {
    const fmt = (v) => this.format(v)
    const dense = this.pointsValue.length > 40
    const datasets = this.sparklineValue
      ? this.sparklineDatasets()
      : this.smoothValue
        ? this.smoothDatasets(dense)
        : this.plainDatasets(dense)

    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: { labels: this.labelsValue, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: this.sparklineValue ? 4 : 0 },
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: this.smoothValue, labels: { color: MUTED, boxWidth: 12, font: { size: 11 } } },
          decimation: { enabled: true, algorithm: "lttb" },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const name = ctx.dataset.label ? ctx.dataset.label + ": " : ""
                const pr = this.isPr(ctx.dataIndex) && ctx.dataset.label !== "Trend" ? " · 🏆 PR" : ""
                return `${name}${fmt(ctx.parsed.y)}${pr}`
              },
            },
          },
        },
        scales: this.sparklineValue
          ? { x: { display: false }, y: { display: false } }
          : {
              x: { grid: { display: false }, ticks: { maxTicksLimit: 6, color: MUTED, font: { size: 10 } } },
              y: { grid: { color: GRID }, ticks: { color: MUTED, font: { size: 10 }, callback: fmt } },
            },
      },
    })
  }

  sparklineDatasets() {
    return [
      {
        data: this.pointsValue,
        borderColor: this.colorValue,
        backgroundColor: this.rgba(this.colorValue, 0.14),
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        spanGaps: true,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: "#fff",
        pointBorderColor: this.colorValue,
        pointBorderWidth: 2,
      },
    ]
  }

  plainDatasets(dense) {
    return [
      {
        data: this.pointsValue,
        borderColor: this.colorValue,
        backgroundColor: this.fillValue ? this.rgba(this.colorValue, 0.15) : "transparent",
        borderWidth: 2.5,
        tension: 0.35,
        fill: this.fillValue,
        spanGaps: true,
        pointRadius: (ctx) => (this.isPr(ctx.dataIndex) ? 5 : dense ? 0 : 3),
        pointHoverRadius: 5,
        pointBackgroundColor: "#fff",
        pointBorderColor: this.colorValue,
        pointBorderWidth: (ctx) => (this.isPr(ctx.dataIndex) ? 2.5 : 2),
      },
    ]
  }

  isPr(index) {
    return this.hasPrsValue && this.prsValue[index] === true
  }

  smoothDatasets(dense) {
    const window = Math.max(3, Math.round(this.pointsValue.length / 40))
    return [
      {
        label: "Trend",
        data: this.movingAverage(this.pointsValue, window),
        borderColor: this.colorValue,
        backgroundColor: this.fillValue ? this.rgba(this.colorValue, 0.12) : "transparent",
        borderWidth: 3,
        tension: 0.4,
        fill: this.fillValue,
        spanGaps: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        order: 1,
      },
      {
        label: "Actual",
        data: this.pointsValue,
        borderColor: this.rgba(this.colorValue, 0.25),
        borderWidth: 1,
        tension: 0.2,
        fill: false,
        spanGaps: true,
        pointRadius: (ctx) => (this.isPr(ctx.dataIndex) ? 5 : dense ? 0 : 2),
        pointHoverRadius: 5,
        pointBackgroundColor: "#fff",
        pointBorderColor: (ctx) => (this.isPr(ctx.dataIndex) ? this.colorValue : this.rgba(this.colorValue, 0.4)),
        pointBorderWidth: (ctx) => (this.isPr(ctx.dataIndex) ? 2.5 : 1.5),
        order: 2,
      },
    ]
  }

  // Centered moving average; ignores nulls in the window.
  movingAverage(data, window) {
    const half = Math.floor(window / 2)
    return data.map((_, i) => {
      let sum = 0
      let count = 0
      for (let j = i - half; j <= i + half; j++) {
        if (data[j] != null) {
          sum += data[j]
          count++
        }
      }
      return count ? Math.round((sum / count) * 10) / 10 : null
    })
  }

  format(v) {
    if (v == null) return ""
    if (this.unitValue === "tonnes") return `${(v / 1000).toFixed(1)}t`
    if (this.unitValue === "kg") return `${Math.round(v)} kg`
    return `${Math.round(v)}`
  }

  rgba(hex, alpha) {
    const n = parseInt(hex.replace("#", ""), 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
  }

  disconnect() {
    this.chart?.destroy()
  }
}
